import React, { useState, useEffect } from 'react';
import { FileText, Clock, Monitor, ListChecks, Download, RotateCcw, Filter } from 'lucide-react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  useActivityRetry,
  useActivityRollback,
  useRemediationPlanBanner,
  useRemediationPlanAgentDetails,
  useRemediationPlanPatchSummary,
  useRemediationPlanPatchDevices,
} from '../services/activityService';
import { useAuth } from 'oidc-react';
import { useTenantSelection } from '../../providers/TenantSelectionProvider';
import { useAgentHeartBeatService } from '../services/agentHeartBeatService';
import { useDeviceManagementService } from '../services/deviceManagementService';
import { AgentStatusIndicator } from '../AgentStatus';
import moment from 'moment';
import api from '../services/apiService';
import Project from '../../common/Utils/project';
import { useToast } from '../../providers/ToastProvider/ToastContext';
import { usePermissions } from '../../hooks/usePermissions';
import Pagination from '../Pagination/Pagination';
import { getConditionalStatusBadgeClass, getStatusDisplayText, isFailedStatus as isFailedStatusColor } from '../../common/Utils/color';
import SegmentedControl from '../SegmentedControl/SegmentedControl';
import UtilizationBars from '../UtilizationBars/UtilizationBars';
import {
  VIEW_MODE_OPTIONS,
  VIEW_MODE_COLOR_SCHEME,
  ViewMode,
  REPORT_VIEW_MODE_OPTIONS,
  REPORT_VIEW_MODE_COLOR_SCHEME,
  ReportViewMode,
} from './patchCenter.config';
import SlideOverPanel from './SlideOverPanel';
import DeviceInsightTable from './DeviceInsightTable';
import PatchInsightPanel, { PatchDeviceRow } from './PatchInsightPanel';
import PatchStatusBar from './PatchStatusBar';
import Tooltip from '../Tooltip/Tooltip';

// Shows '-' only when the duration is a negative number; otherwise shows the real value.
const formatPatchDuration = (value: string | null): string => {
  if (value === null || value === undefined || value === '') return 'N/A';
  const num = parseFloat(value);
  if (!isNaN(num) && num < 0) return '-';
  return value;
};

type Device = {
  id: string;
  remediationPlanId: string;
  hostName: string;
  startTime: string;
  endTime: string;
  cpuUtilization: string | null;
  ramUtilization: string | null;
  patchDuration: string | null;
  status: string | null;
  condition: string | null;
  isAgentActive?: boolean;
  statusMessage?: string;
};

const formatDateTime = (value?: string | null): string => {
  if (!value || value.startsWith('0001-01-01')) return 'N/A';
  return moment(value).format('DD MMM YYYY, hh:mm A');
};

// Friendly "Type" column label per backend RemediationPlanActionType enum name.
const TASK_TYPE_LABELS: Record<string, string> = {
  SoftwareInstall: 'Software Install',
  SoftwareUpdate: 'Software Update',
  SoftwareUnInstall: 'Software Uninstall',
  WindowsOSUpdate: 'Windows Update',
  MacOSUpdate: 'macOS Update',
  LinuxOSUpdate: 'Linux Update',
  Script: 'Script',
  RegistryUpdate: 'Registry Update',
  RebootDevice: 'Reboot',
  ShutdownDevice: 'Shutdown',
  EndpointSync: 'Endpoint Sync',
  ShipAgentLog: 'Ship Agent Logs',
  InstallPatchifiAgent: 'Agent Install',
  GetNearByDiscoverableDevices: 'Discovery',
};

// The backend already phrases the full activity (e.g. "Install Chrome v125.0", "Windows Update
// [KB123] - Title") into one field, mirroring DeviceInsightTable's Activity column - no more
// reconstructing a label from separate packageName/packageVersion/kbId/kbTitle fields here.
// previousVersion/targetVersion are the same versions already folded into that text, broken back
// out separately purely so the Version column can color-code the transition.
const describePatchSummary = (item: any): PatchGroup => ({
  taskId: item.taskId,
  label: item.activity || 'Activity',
  type: TASK_TYPE_LABELS[item.taskType] || item.taskType || 'Activity',
  rawType: item.taskType || '',
  previousVersion: item.previousVersion || undefined,
  targetVersion: item.targetVersion || undefined,
  deviceCount: item.deviceCount || 0,
  statusCounts: {
    completed: item.statusCounts?.completed || 0,
    inProgress: item.statusCounts?.inProgress || 0,
    errors: item.statusCounts?.errors || 0,
    unknown: item.statusCounts?.unknown || 0,
  },
});

type PatchGroup = {
  taskId: string;
  label: string;
  type: string;
  // Raw backend RemediationPlanActionType (e.g. "SoftwareUpdate") - `type` above is already the
  // friendly label, but the per-device Activity column needs the raw enum to branch on.
  rawType: string;
  previousVersion?: string;
  targetVersion?: string;
  deviceCount: number;
  statusCounts: { completed: number; inProgress: number; errors: number; unknown: number };
};

const ReportView: React.FC = () => {
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<string[]>([]);
  const [selectAll, setSelectAll] = useState(false);
  const [devices, setDevices] = useState<Device[]>([]);
  const [loading, setLoading] = useState(true);
  const [devicesLoading, setDevicesLoading] = useState(false);
  const [deviceTotalPages, setDeviceTotalPages] = useState(1);
  const [remediationPlan, setRemediationPlan] = useState<any>(null);
  const [showFailedOnly, setShowFailedOnly] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  // reportView (device/patch) and displayMode (compact/detailed) are distinct from this page's
  // inherited view/mode params (table/card + Automated/OnDemand, carried over from the list page
  // so the back button restores it) - reusing those names here would clobber that state.
  const [viewMode, setViewMode] = useState<ViewMode>(searchParams.get('displayMode') === 'detailed' ? 'detailed' : 'compact');
  const [reportViewMode, setReportViewMode] = useState<ReportViewMode>(searchParams.get('reportView') === 'patch' ? 'patch' : 'device');
  const [patchGroups, setPatchGroups] = useState<PatchGroup[]>([]);
  const [patchLoading, setPatchLoading] = useState(false);
  const [patchTotalPages, setPatchTotalPages] = useState(1);
  const [devicePanel, setDevicePanel] = useState<{ agentId: string; hostName: string; remediationPlanId?: string } | null>(null);
  const [patchPanel, setPatchPanel] = useState<{ taskId: string; label: string; type: string; rawType: string } | null>(null);
  const [patchDeviceRows, setPatchDeviceRows] = useState<PatchDeviceRow[]>([]);
  const [patchDeviceLoading, setPatchDeviceLoading] = useState(false);
  const [patchDeviceCurrentPage, setPatchDeviceCurrentPage] = useState(1);
  const [patchDeviceTotalPages, setPatchDeviceTotalPages] = useState(1);

  const auth = useAuth();
  const { selectedTenantId } = useTenantSelection();
  const { getAgentHeartbeatStatus } = useAgentHeartBeatService();
  const { id } = useParams<{ id: string }>();
  const { getRemediationPlanBanner } = useRemediationPlanBanner();
  const { getRemediationPlanAgentDetails } = useRemediationPlanAgentDetails();
  const { getRemediationPlanPatchSummary } = useRemediationPlanPatchSummary();
  const { getRemediationPlanPatchDevices } = useRemediationPlanPatchDevices();
  const { getRollbackActivity } = useActivityRollback();
  const [action, setAction] = useState('');
  const { addToast } = useToast();
  const { retryActivity } = useActivityRetry();
  const [retryLoadingId, setRetryLoadingId] = useState<string | null>(null);
  const { canEdit } = usePermissions();
  const canRetry = canEdit('remediationPlan');
  const [currentPage, setCurrentPage] = useState(1);
  const [statusesLoading, setStatusesLoading] = useState(false);
  const itemsPerPage = 20;

  // Banner: plan header + agent/activity counts. Cached server-side (1hr) - cheap on every page load.
  useEffect(() => {
    const fetchBanner = async () => {
      if (!id || !selectedTenantId) return;
      try {
        setLoading(true);
        const response = await getRemediationPlanBanner(selectedTenantId as string, id);
        setRemediationPlan(response?.data ?? null);
      } catch (error) {
        console.error('Failed to load remediation plan banner', error);
      } finally {
        setLoading(false);
      }
    };

    fetchBanner();
  }, [id, selectedTenantId]);

  // Device View: backend-paginated per-device rows - only the current page is Cosmos-enriched server-side.
  useEffect(() => {
    if (reportViewMode !== 'device' || !id || !selectedTenantId) return;

    const fetchDevices = async () => {
      try {
        setDevicesLoading(true);
        const response = await getRemediationPlanAgentDetails(
          selectedTenantId as string,
          id,
          currentPage,
          itemsPerPage,
          viewMode === 'compact' ? 'Compact' : 'Detailed',
        );

        const mappedDevices: Device[] = (response?.data || []).map((item: any) => ({
          id: item.tenantAgentId,
          remediationPlanId: item.remediationPlanId,
          hostName: item.hostName,
          startTime:
            item.remediationPlanStartTime && !item.remediationPlanStartTime.startsWith('0001-01-01')
              ? moment(item.remediationPlanStartTime).format('DD MMM YYYY, hh:mm A')
              : 'N/A',
          endTime:
            item.remediationPlanEndTime && !item.remediationPlanEndTime.startsWith('0001-01-01')
              ? moment(item.remediationPlanEndTime).format('DD MMM YYYY, hh:mm A')
              : 'N/A',
          cpuUtilization: item.cpuUtilizationPercent,
          ramUtilization: item.ramUsagePercent,
          patchDuration: item.remediationPlanDurationInMinutes?.toString(),
          status: item.agentRemediationPlanStatus,
          condition: '',
        }));

        setDevices(mappedDevices);
        setDeviceTotalPages(Math.max(1, response?.pagination?.totalPages || 1));
      } catch (error) {
        console.error('Failed to load device page', error);
      } finally {
        setDevicesLoading(false);
      }
    };

    fetchDevices();
  }, [reportViewMode, id, selectedTenantId, currentPage, viewMode]);

  const fetchAgentStatuses = async () => {
    if (devices.length === 0) return;

    try {
      setStatusesLoading(true);
      const agentIds = devices.map((device) => device.id);
      const heartbeatResponse = await getAgentHeartbeatStatus(selectedTenantId as string, agentIds);
      const statusData = heartbeatResponse?.isSuccess && Array.isArray(heartbeatResponse.data) ? heartbeatResponse.data : [];

      const enrichedDevices = devices.map((device) => {
        const agent = statusData.find((item: any) => item.tenantAgentId === device.id);
        return {
          ...device,
          isAgentActive: agent?.agentStatus === 'Active',
          statusMessage: agent?.heartbeatStatusMessage || '',
        };
      });

      setDevices(enrichedDevices);
    } catch (error) {
      console.error('Error fetching agent statuses:', error);
    } finally {
      setStatusesLoading(false);
    }
  };

  // Depend on currentPage too, not just devices.length - two consecutive pages can be the same size
  // (e.g. two full 20-item pages), which would otherwise silently skip the heartbeat refresh.
  useEffect(() => {
    if (devices.length > 0) {
      fetchAgentStatuses();
    }
  }, [devices.length, currentPage]);

  const isFailedStatus = (status: string | null | undefined) => {
    if (!status) return false;
    const s = status.toLowerCase();
    return s.includes('fail') || s.includes('error') || s.includes('timeout');
  };

  // Patch View: backend-paginated summary, one row per patch/action/OS-update sub-task with a
  // device-count + status breakdown - replaces the old one-call-per-device fan-out.
  useEffect(() => {
    if (reportViewMode !== 'patch' || !id || !selectedTenantId) return;

    let cancelled = false;

    const fetchPatchSummary = async () => {
      try {
        setPatchLoading(true);
        const response = await getRemediationPlanPatchSummary(selectedTenantId as string, id, currentPage, itemsPerPage);
        if (cancelled) return;
        const mapped: PatchGroup[] = (response?.data || []).map((item: any) => describePatchSummary(item));
        setPatchGroups(mapped);
        setPatchTotalPages(Math.max(1, response?.pagination?.totalPages || 1));
      } catch (error) {
        console.error('Failed to load patch summary', error);
        if (!cancelled) setPatchGroups([]);
      } finally {
        if (!cancelled) setPatchLoading(false);
      }
    };

    fetchPatchSummary();
    return () => {
      cancelled = true;
    };
  }, [reportViewMode, id, selectedTenantId, currentPage]);

  // Patch drill-down: backend-paginated device rows for whichever patch is open in the slide-over.
  useEffect(() => {
    if (!patchPanel || !id || !selectedTenantId) return;

    let cancelled = false;

    const fetchPatchDevices = async () => {
      try {
        setPatchDeviceLoading(true);
        const response = await getRemediationPlanPatchDevices(
          selectedTenantId as string,
          id,
          patchPanel.taskId,
          patchDeviceCurrentPage,
          itemsPerPage,
        );
        if (cancelled) return;
        const rows: PatchDeviceRow[] = (response?.data || []).map((item: any) => ({
          agentId: item.tenantAgentId,
          hostName: item.hostName,
          status: item.agentTaskStatus,
          startTime: formatDateTime(item.remediationPlanTaskStartTime),
          endTime: formatDateTime(item.remediationPlanTaskEndTime),
          cpuUsage: item.cpuUtilizationPercent != null ? `${item.cpuUtilizationPercent}%` : 'NA',
          memoryUsage: item.ramUsagePercent != null ? `${item.ramUsagePercent}%` : 'NA',
          activity: item.taskMessage || '',
          stackTrace: item.stackTrace || '',
          packageDetail: item.packageDetail || null,
          windowsUpdate: item.windowsUpdate || null,
          scriptExecution: item.scriptExecution || null,
          registryUpdate: item.registryUpdate || null,
          restartAgent: item.restartAgent || null,
          shutdown: item.shutdown || null,
          endpointSync: item.endpointSync || null,
          shipAgentLogsTask: item.shipAgentLogsTask || null,
        }));
        setPatchDeviceRows(rows);
        setPatchDeviceTotalPages(Math.max(1, response?.pagination?.totalPages || 1));
      } catch (error) {
        console.error('Failed to load patch device details', error);
        if (!cancelled) setPatchDeviceRows([]);
      } finally {
        if (!cancelled) setPatchDeviceLoading(false);
      }
    };

    fetchPatchDevices();
    return () => {
      cancelled = true;
    };
  }, [patchPanel, id, selectedTenantId, patchDeviceCurrentPage]);

  useEffect(() => {
    setPatchDeviceCurrentPage(1);
  }, [patchPanel?.taskId]);

  // devices/patchGroups are already one server-paginated page each, already filtered server-side by
  // viewMode (compact/detailed) - these client-side filters just narrow within that page for
  // search/showFailedOnly (search/status filtering across the whole plan would need backend
  // query-param support, which isn't wired up yet).
  const filteredData = devices
    .filter((item) => {
      if (showFailedOnly) return isFailedStatus(item.status);
      return true;
    })
    .filter((item) => item.hostName.toLowerCase().includes(search.toLowerCase()));

  const filteredPatchGroups = patchGroups
    .filter((group) => (showFailedOnly ? group.statusCounts.errors > 0 : true))
    .filter((group) => group.label.toLowerCase().includes(search.toLowerCase()));

  const totalPages = reportViewMode === 'device' ? deviceTotalPages : patchTotalPages;

  useEffect(() => {
    setCurrentPage(1);
  }, [reportViewMode, viewMode]);

  // Keep reportView/displayMode in the URL so refresh, share, or browser back/forward restores
  // whichever device-vs-patch and compact-vs-detailed combination the user had open.
  useEffect(() => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('reportView', reportViewMode);
    newParams.set('displayMode', viewMode);
    setSearchParams(newParams, { replace: true });
  }, [reportViewMode, viewMode]);

  const getSquareStyle = (isActive: boolean) => ({
    width: '20px',
    height: '20px',
    borderRadius: '4px',
    border: isActive ? '#00FF00' : '5px solid #444',
    backgroundColor: isActive ? '#00FF00' : 'transparent',
    cursor: 'pointer',
    display: 'inline-block',
    marginLeft: '35px',
  });

  const handleRowSelect = (id: string) => {
    setSelected((prev) => (prev.includes(id) ? prev.filter((h) => h !== id) : [...prev, id]));
  };

  const handleSelectAll = () => {
    const pageIds = filteredData.map((d) => d.id);
    if (selectAll) {
      // unselect current page ids
      setSelected((prev) => prev.filter((id) => !pageIds.includes(id)));
    } else {
      setSelected((prev) => Array.from(new Set([...prev, ...pageIds])));
    }
    setSelectAll(!selectAll);
  };

  useEffect(() => {
    setSelectAll(filteredData.length > 0 && filteredData.every((d) => selected.includes(d.id)));
  }, [selected, filteredData]);
  useEffect(() => {
    setSelected((prev) => prev.filter((id) => filteredData.some((d) => d.id === id)));
  }, [showFailedOnly, search, devices, viewMode]);

  if (loading) {
    return <div className="text-white p-6">Loading...</div>;
  }
  const handleSubmitAction = () => {
    if (selected.length === 0) {
      alert('Please select at least one device.');
      return;
    }

    switch (action) {
      case 'Reboot':
        performReboot();
        break;

      case 'ShutDown':
        performShutdown();
        break;

      case 'Rollback':
        performRollback(); // API commented
        break;

      default:
        alert('Please select an action.');
    }
  };

  // ---------------------- ACTION API CALLS ----------------------

  const performReboot = async () => {
    if (selected.length === 0) return; // avoid wrong calls

    try {
      const payload = {
        agentIdList: selected,
      };

      await api.post(`${Project.api}patchcenterservice/RemediationPlan/${selectedTenantId}/reboot-device-list`, JSON.stringify(payload));

      // console.log("Reboot initiated");
      addToast('Reboot initiated successfully', 'success', 3000, 'Reboot');
    } catch (error) {
      console.error('Reboot failed', error);
    }
  };

  const performShutdown = async () => {
    if (selected.length === 0) return;

    try {
      const payload = {
        agentIdList: selected,
      };

      await api.post(`${Project.api}patchcenterservice/RemediationPlan/${selectedTenantId}/shutdown-device-list`, JSON.stringify(payload));

      // console.log("Shutdown initiated");
      addToast('Shutdown initiated successfully', 'success', 3000, 'Shutdown');
    } catch (error) {
      console.error('Shutdown failed', error);
    }
  };

  // Placeholder for Rollback API — you will provide later
  const performRollback = async () => {
    if (selected.length === 0) return;

    try {
      const res = await getRollbackActivity(selectedTenantId as string, id as string, selected);
      // console.log('Rollback success:', res);
      addToast('Rollback initiated successfully', 'success', 3000, 'Rollback');
    } catch (error) {
      console.error('Rollback failed:', error);
    }

    console.log('Rollback action triggered (API pending)');
  };

  const handleShowFailed = () => {
    setShowFailedOnly((prev) => !prev);
  };

  const handleDwonloadReport = async () => {
    if (!id || !selectedTenantId) return;

    try {
      // devices only holds the currently-loaded page under server-side pagination - pull every page
      // fresh so the CSV always reflects the whole plan, not just what's on screen right now.
      const pageSize = 100;
      const firstPage = await getRemediationPlanAgentDetails(selectedTenantId as string, id, 1, pageSize);
      const totalDevicePages = Math.max(1, firstPage?.pagination?.totalPages || 1);
      const remainingPages = await Promise.all(
        Array.from({ length: totalDevicePages - 1 }, (_, idx) =>
          getRemediationPlanAgentDetails(selectedTenantId as string, id, idx + 2, pageSize),
        ),
      );
      const allRows: any[] = [firstPage, ...remainingPages].flatMap((page) => page?.data || []);

      if (allRows.length === 0) {
        addToast('No devices to export.', 'info', 3000, 'Download Report');
        return;
      }

      const header = Object.keys(allRows[0]).join(',');
      const csv = [
        header,
        ...allRows.map((row) =>
          Object.values(row)
            .map((value) => `"${value ?? ''}"`) // wrap with quotes
            .join(','),
        ),
      ].join('\n');

      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.setAttribute(
        'download',
        remediationPlan?.remediationPlanName ? `${remediationPlan.remediationPlanName}_Report.csv` : 'Report.csv',
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Failed to download report', error);
      addToast('Failed to download report', 'error', 3000, 'Download Report');
    }
  };
  const handleRetry = async (remediationPlanId: string) => {
    const hasFailed = devices.some((d) => d.status?.toLocaleLowerCase() === 'CompletedWithErrors'.toLocaleLowerCase());

    if (!hasFailed) {
      addToast('No failed plans to retry.', 'warning', 3000, 'Retry');
      return;
    }
    try {
      setRetryLoadingId(remediationPlanId);
      const res = await retryActivity(remediationPlanId.toString());
      console.log('Retry success:', res);
      addToast('Retry initiated successfully', 'success', 3000, 'Retry');
    } catch (error) {
      console.error('Retry failed:', error);
      addToast('Retry failed', 'error', 3000, 'Retry');
    } finally {
      setRetryLoadingId(null);
    }
  };
  const handleDeviceInsightsNavvigation = (agentId: string, deviceStatus: string | null, hostName: string, remediationPlanId?: string) => {
    if (deviceStatus === 'NotSubmitted') return;
    setDevicePanel({ agentId, hostName, remediationPlanId });
  };
  return (
    <div className="bg-[var(--Background-Color,#161617)] text-white p-6 rounded-xl shadow-md space-y-6">
      {/* Header */}
      <div
        className="flex flex-col md:flex-row justify-between gap-5 p-5 rounded-xl border"
        style={{ backgroundColor: '#1a1a1a', borderColor: '#2a2a2a' }}
      >
        <div className="space-y-3">
          <div className="flex items-center gap-2 flex-wrap">
            <FileText size={15} className="text-gray-500 flex-shrink-0" />
            <span className="text-[11px] uppercase tracking-wider text-gray-500 font-medium">Remediation Plan</span>
            <span
              className="px-3 py-1 rounded-full text-white text-xs font-medium border"
              style={{ backgroundColor: '#252525', borderColor: '#333' }}
            >
              {remediationPlan?.remediationPlanName || 'N/A'}
            </span>
          </div>

          <div className="flex flex-wrap gap-x-8 gap-y-2">
            <div className="flex items-center gap-2">
              <Clock size={14} className="text-gray-500 flex-shrink-0" />
              <span className="text-xs text-gray-500">Time Activated</span>
              <span className="text-xs text-gray-200 font-medium">
                {remediationPlan?.remediationPlanCreatedTime
                  ? new Date(remediationPlan.remediationPlanCreatedTime).toLocaleString()
                  : 'N/A'}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Monitor size={14} className="text-gray-500 flex-shrink-0" />
              <span className="text-xs text-gray-500">Devices</span>
              <span className="text-xs text-gray-200 font-medium">{remediationPlan?.agentCount ?? 0}</span>
            </div>
            <div className="flex items-center gap-2">
              <ListChecks size={14} className="text-gray-500 flex-shrink-0" />
              <span className="text-xs text-gray-500">Activities</span>
              <span className="text-xs text-gray-200 font-medium">{remediationPlan?.activityCount ?? 0}</span>
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-2 flex-shrink-0">
          <button
            className="flex items-center justify-center gap-2 bg-[#D12929] hover:bg-[#b91f1f] text-white text-sm font-medium py-2 px-4 rounded-full transition-colors whitespace-nowrap"
            onClick={() => handleDwonloadReport()}
          >
            <Download size={15} />
            Download Report
          </button>
          <button
            className="flex items-center justify-center gap-2 border border-[#C62626] text-[#f87171] hover:bg-[#C62626] hover:text-white text-sm font-medium py-2 px-4 rounded-full transition-colors whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-[#f87171]"
            onClick={() => id && handleRetry(id)}
            disabled={
              !canRetry ||
              // Disable if any device has a different remediationPlanId than the parent
              devices.some((d) => d.remediationPlanId !== remediationPlan?.remediationPlanId)
            }
          >
            <RotateCcw size={15} />
            Retry Failed Updates
          </button>
          <button
            className={`flex items-center justify-center gap-2 border text-sm font-medium py-2 px-4 rounded-full transition-colors whitespace-nowrap ${
              showFailedOnly
                ? 'bg-[#D12929] text-white border-[#D12929]' // active state
                : 'border-[#3a3a3a] text-gray-300 hover:bg-white hover:text-black' // inactive state
            }`}
            onClick={() => handleShowFailed()}
          >
            <Filter size={15} />
            Show Failed
          </button>
        </div>
      </div>

      {/* Device / Patch toggle */}
      <div className="flex items-center justify-between">
        <SegmentedControl
          options={REPORT_VIEW_MODE_OPTIONS}
          value={reportViewMode}
          onChange={(v) => setReportViewMode(v as ReportViewMode)}
          colorScheme={REPORT_VIEW_MODE_COLOR_SCHEME}
        />
      </div>

      {/* Search + Bulk Actions */}
      <div className="mt-4">
        <div className="flex items-center justify-between bg-[#1C1C1D] px-3 py-2 rounded text-white mb-3">
          {/* LEFT → Search */}
          <input
            type="text"
            placeholder={reportViewMode === 'device' ? 'Search' : 'Search patches'}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                setSearch(searchInput.trim());
                setCurrentPage(1);
              }
            }}
            className="bg-[#2A2A2D] text-white border border-[#3A3A40]
             rounded-lg px-4 py-2.5 text-base
             w-80 md:w-96 outline-none
             focus:border-red-500 transition"
          />

          {/* RIGHT → Actions */}
          {reportViewMode === 'device' && (
            <div className="flex items-center gap-2">
              <SegmentedControl
                options={VIEW_MODE_OPTIONS}
                value={viewMode}
                onChange={(v) => setViewMode(v as ViewMode)}
                colorScheme={VIEW_MODE_COLOR_SCHEME}
              />
            </div>
          )}
        </div>
        {/* Active Filters */}
        {(search || showFailedOnly) && (
          <div className="flex items-center gap-2 mb-3 px-3 flex-wrap">
            {/* Search Chip */}
            {search && (
              <div className="flex items-center bg-red-600/20 border border-red-500 px-3 py-1 rounded-full text-sm">
                <span className="mr-2">Search: {search}</span>
                <button
                  onClick={() => {
                    setSearch('');
                    setSearchInput('');
                  }}
                  className="text-red-300 hover:text-white"
                >
                  ✕
                </button>
              </div>
            )}

            {/* Failed Filter Chip */}
            {showFailedOnly && (
              <div className="flex items-center bg-red-600/20 border border-red-500 px-3 py-1 rounded-full text-sm">
                <span className="mr-2">Failed Only</span>
                <button onClick={() => setShowFailedOnly(false)} className="text-red-300 hover:text-white">
                  ✕
                </button>
              </div>
            )}

            {/* Clear All */}
            <button
              onClick={() => {
                setSearch('');
                setSearchInput('');
                setShowFailedOnly(false);
              }}
              className="text-xs text-gray-400 underline hover:text-white ml-2"
            >
              Clear All
            </button>
          </div>
        )}
        {/* Table */}
        {reportViewMode === 'device' ? (
          <div className="overflow-auto rounded-lg shadow">
            <table className="w-full text-sm text-left">
              <thead style={{ backgroundColor: '#161617' }}>
                <tr>
                  <th className="p-3" onClick={handleSelectAll}>
                    <div style={getSquareStyle(selectAll)} />
                  </th>
                  <th className="p-3">Host Name</th>
                  <th className="p-3">Start Time</th>
                  <th className="p-3">End Time</th>
                  <th className="p-3">Utilization</th>
                  <th className="p-3 text-center">Patch Duration</th>
                  <th className="p-3 text-center">Agent Status</th>
                  <th className="p-3 text-center">Status</th>
                </tr>
              </thead>
              <tbody>
                {devicesLoading ? (
                  <tr>
                    <td colSpan={8} className="p-6 text-center text-gray-400">
                      Loading devices...
                    </td>
                  </tr>
                ) : filteredData.length > 0 ? (
                  filteredData.map((device, index) => (
                    <tr
                      key={device.id}
                      style={{
                        backgroundColor: index % 2 === 0 ? '#2E2D2D' : '#1D1D1D',
                      }}
                    >
                      <td className="p-3" onClick={() => handleRowSelect(device.id)}>
                        <div style={getSquareStyle(selected.includes(device.id))} />
                      </td>
                      <td
                        className="p-3 cursor-pointer"
                        onClick={() => handleDeviceInsightsNavvigation(device.id, device.status, device.hostName, device.remediationPlanId)}
                      >
                        {device.hostName}
                      </td>
                      <td className="p-3">{device.startTime}</td>
                      <td className="p-3">{device.endTime}</td>
                      <td className="p-3">
                        <UtilizationBars cpu={device.cpuUtilization} ram={device.ramUtilization} />
                      </td>
                      <td className="p-3 text-center">{formatPatchDuration(device.patchDuration)}</td>
                      <td className="p-3 text-center">
                        <AgentStatusIndicator
                          agentId={device.id}
                          isActive={device.isAgentActive}
                          statusMessage={device.statusMessage}
                          isLoading={statusesLoading}
                        />
                      </td>
                      <td className="p-3 text-center">
                        <span className={getConditionalStatusBadgeClass(device.status)}>{getStatusDisplayText(device.status)}</span>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={8} className="p-6 text-center text-gray-400">
                      No devices found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="overflow-auto rounded-lg shadow">
            <table className="w-full text-sm text-left">
              <thead style={{ backgroundColor: '#161617' }}>
                <tr>
                  <th className="p-3">Patch</th>
                  <th className="p-3">Type</th>
                  <th className="p-3 text-center">Status</th>
                </tr>
              </thead>
              <tbody>
                {patchLoading ? (
                  <tr>
                    <td colSpan={3} className="p-6 text-center text-gray-400">
                      Loading patch data...
                    </td>
                  </tr>
                ) : filteredPatchGroups.length > 0 ? (
                  filteredPatchGroups.map((group, index) => (
                    <tr
                      key={group.taskId}
                      className={group.deviceCount === 0 ? 'cursor-default' : 'cursor-pointer'}
                      style={{
                        backgroundColor: index % 2 === 0 ? '#2E2D2D' : '#1D1D1D',
                      }}
                      onClick={() => {
                        if (group.deviceCount === 0) return;
                        setPatchPanel({ taskId: group.taskId, label: group.label, type: group.type, rawType: group.rawType });
                      }}
                    >
                      <td className="p-3">
                        {group.deviceCount === 0 ? (
                          <Tooltip message="Agents associated to this plan do not require this patch">
                            <span>
                              {group.label}
                              {group.targetVersion && <span className="text-green-400"> -&gt; {group.targetVersion}</span>}
                            </span>
                          </Tooltip>
                        ) : (
                          <span>
                            {group.label}
                            {group.targetVersion && <span className="text-green-400"> -&gt; {group.targetVersion}</span>}
                          </span>
                        )}
                      </td>
                      <td className="p-3">{group.type}</td>
                      <td className="p-3 flex justify-center">
                        <PatchStatusBar
                          completed={group.statusCounts.completed}
                          inProgress={group.statusCounts.inProgress}
                          errors={group.statusCounts.errors}
                          unknown={group.statusCounts.unknown}
                          totalDevices={group.deviceCount}
                        />
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={3} className="p-6 text-center text-gray-400">
                      No patches found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          loading={reportViewMode === 'device' ? devicesLoading : patchLoading}
          onPageChange={(p) => setCurrentPage(p)}
        />
      </div>

      <SlideOverPanel isOpen={!!devicePanel} onClose={() => setDevicePanel(null)} title="Device Insight" subtitle={devicePanel?.hostName}>
        {devicePanel && (
          <DeviceInsightTable remediationPlanIdProp={devicePanel.remediationPlanId ?? id} agentIdProp={devicePanel.agentId} embedded />
        )}
      </SlideOverPanel>

      <SlideOverPanel
        isOpen={!!patchPanel}
        onClose={() => {
          setPatchPanel(null);
          setPatchDeviceRows([]);
        }}
        title="Patch Insight"
        subtitle={patchPanel?.label}
      >
        {patchPanel && (
          <PatchInsightPanel
            taskId={patchPanel.taskId}
            patchLabel={patchPanel.label}
            patchType={patchPanel.type}
            rawTaskType={patchPanel.rawType}
            rows={patchDeviceRows}
            loading={patchDeviceLoading}
            currentPage={patchDeviceCurrentPage}
            totalPages={patchDeviceTotalPages}
            onPageChange={setPatchDeviceCurrentPage}
          />
        )}
      </SlideOverPanel>
    </div>
  );
};

export default ReportView;
